import { makeObservable, observable, computed, action } from 'mobx'
import AsyncStore from 'stores/AsyncStore'
import FileInputStore from 'stores/FileInputStore'
import InputStore from 'stores/InputStore'
import uuid from 'react-uuid'
import CustomerSelfManagementService from 'services/CustomerSelfManagementService'

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
const SUPPORTED_FORMATS = 'JPG, PNG, PDF';

export const PAYMENT_REJECTION_STATUS = {
    NOTIFIED: 'notified',
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    REJECTED: 'rejected',
    IN_DEBT: 'inDebt',
    UPDATE_PAYMENT: 'updatePayment',
}

export const COLLECTIONS_STATUS = {
    WITHOUT_REJECTIONS: 'withoutRejections',
    WITH_REJECTIONS: 'withRejections',
    WITHOUT_FINANCIAL_COVERAGE: 'withoutFinancialCoverage',
    PRE_REJECTION: 'preRejection',
    EXPIRED: 'expired',
}

class PaymentRejectionStore extends AsyncStore {
    constructor(parentStore = null) {
        super()
        this.parentStore = parentStore

        this.collections = { status: null, message: null }
        this.paymentRejections = []
        this.paymentMethods = []

        this.ids = [];
        this.paymentAmount = null;

        this.files = [];
        this.comment = new InputStore();
        this.type = null;
        this.validationError = null;

        this.customerSelfManagementService = new CustomerSelfManagementService()

        this.fileStores = [
            new FileInputStore({
                id: uuid(),
                acceptedTypeList: ACCEPTED_TYPES,
                maxFileSize: MAX_FILE_SIZE,
                supportedFormats: SUPPORTED_FORMATS,
            })
        ];

        this.makeObservables()
    };

    makeObservables() {
        makeObservable(this, {
            collections: observable,
            paymentRejections: observable,
            paymentMethods: observable,
            ids: observable,
            paymentAmount: observable,
            files: observable,
            comment: observable,
            type: observable,
            validationError: observable,
            fileStores: observable,
            isValid: computed,
            setCollections: action,
            setPaymentRejections: action,
            setPaymentMethods: action,
            addFile: action,
            deleteFile: action,
            prepareFiles: action,
            clear: action,
        })
    };

    setIds(value) {
        this.ids = value;
    };

    setPaymentAmount(value) {
        this.paymentAmount = value;
    };

    setFiles(value) {
        this.files = value;
    };

    setComment(value) {
        this.comment = value;
    };

    setType(value) {
        this.type = value;
    };

    setValidationError(value) {
        this.validationError = value;
    };

    setCollections(value) {
        this.collections = value
    };

    setPaymentRejections(value) {
        this.paymentRejections = value
    };

    setPaymentMethods(value) {
        this.paymentMethods = value
    };

    hydrateFromPolicy(policyData) {
        this.setCollections(policyData?.collections || { status: null, message: null });
        this.setPaymentRejections(policyData?.paymentRejections || []);
        this.setPaymentMethods(policyData?.paymentMethods || []);
    };

    get isValid() {
        const selectedStores = this.fileStores.filter((store) => store.fileSelected);
        
        if (selectedStores.length === 0) {
            return false;
        }

        const hasInvalidFile = selectedStores.some((fileStore) => {
            if (!fileStore.fileBase64) {
                return true;
            }
            if (fileStore.file && fileStore.file.size > MAX_FILE_SIZE) {
                return true;
            }
            if (fileStore.error) {
                return true;
            }
            return false;
        });

        return !hasInvalidFile && !this.validationError;
    };

    addFile() {
        const hasEmptySlot = this.fileStores.some((store) => !store.fileSelected);
        if (hasEmptySlot) {
            return;
        }
        this.fileStores.push(
            new FileInputStore({
                id: uuid(),
                acceptedTypeList: ACCEPTED_TYPES,
                maxFileSize: MAX_FILE_SIZE,
                supportedFormats: SUPPORTED_FORMATS,
            })
        );
    };

    deleteFile(deletedFileStore) {
        this.fileStores = this.fileStores.filter((store) => store.id !== deletedFileStore.id);
        if (this.fileStores.length === 0) {
            this.fileStores = [
                new FileInputStore({
                    id: uuid(),
                    acceptedTypeList: ACCEPTED_TYPES,
                    maxFileSize: MAX_FILE_SIZE,
                    supportedFormats: SUPPORTED_FORMATS,
                })
            ];
        }
    };

    async validateFiles() {
        this.setValidationError(null);

        const selectedFiles = this.fileStores.filter((store) => store.fileSelected && store.fileBase64);

        const oversizedFile = selectedFiles.find((fileStore) => 
            fileStore.file && fileStore.file.size > MAX_FILE_SIZE
        )
        
        if (oversizedFile) {
            this.setValidationError(`El archivo "${oversizedFile.file.name}" excede el tamaño máximo de 5MB`);
            return { isValid: false, error: this.validationError };
        }

        return { isValid: true }
    };

    prepareFiles() {
        this.files = this.fileStores
            .filter((store) => store.fileSelected && store.fileBase64)
            .map((store) => ({
                filename: store.file.name,
                mime: store.file.type,
                content: store.fileBase64,
            }))
    };

    async submitPaymentRejection() {
        this.preRequest(this.submitPaymentRejection);

        try {
            this.prepareFiles();

            const body = {
                files: this.files,
                comment: this.comment?.value,
                type: this.type?.id || this.type,
            };

            const { data: policyData } = await this.customerSelfManagementService.submitPaymentRejection(this.ids, body);

            this.onSuccessRequest();

            return policyData;
        } catch (error) {
            this.processRequestErrors(error);
            this.onErrorRequest(error);
            throw error;
        }
    };

    clear() {
        this.ids = [];
        this.paymentAmount = null;
        this.files = [];
        this.comment = new InputStore();
        this.type = null;
        this.validationError = null;
        this.fileStores = [];
    };
}; 

export default PaymentRejectionStore;
